import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { State } from '../../index';
import { BaseActionTypes } from '../../actions/base.actions';
import {
    Contract2PlausibilitaetActionTypes, DeleteContract2PlausibilitaetResponse,
    GetContract2PlausibilitaetenResponse, PostContract2PlausibilitaetResponse,
} from '../../actions/mdqx/contract2plausibilitaet.actions';
import { HttpService } from '../../../services/http.service';
import { Contract2PlausibilitaetEntity } from '../../../entities/mdqx/contract2plausibilitaet.entity';
import { DaveActions } from '../../actions/actions';
import { getToken } from '../../selectors/base.selectors';

enum ErrorCodes {
    Get = 'Vertragszuordnungen Abrufen fehlgeschlagen',
    Post = 'Vertragszuordnung Anlegen fehlgeschlagen',
    Delete = 'Vertragszuordnung Löschen fehlgeschlagen',
}

@Injectable()
export class Contract2PlausibilitaetEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2PlausibilitaetActionTypes.GetContract2PlausibilitaetenRequest),
        map(
            () => `
            {
                contract2Plausibilitaet {
                    ${Contract2PlausibilitaetEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.contract2Plausibilitaet),
                concatMap(res =>
                    res
                        ? [
                              new GetContract2PlausibilitaetenResponse(
                                  res.map(
                                      contract2Plausibilitaet =>
                                          new Contract2PlausibilitaetEntity(
                                              contract2Plausibilitaet,
                                          ),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2PlausibilitaetActionTypes.PostContract2PlausibilitaetRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContract2Plausibilitaet(
                    contractId: ${JSON.stringify(Payload.contractId)}
                    plausibilitaetId: ${JSON.stringify(Payload.plausibilitaetId)}
                ) {
                    ${Contract2PlausibilitaetEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.createContract2Plausibilitaet),
                concatMap(res =>
                    res
                        ? [
                              new PostContract2PlausibilitaetResponse(
                                  new Contract2PlausibilitaetEntity(res),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(Contract2PlausibilitaetActionTypes.DeleteContract2PlausibilitaetRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContract2Plausibilitaet(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token },
                )
                .pipe(
                    map(res => res && res.deleteContract2Plausibilitaet),
                    concatMap(res =>
                        res
                            ? [new DeleteContract2PlausibilitaetResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                    ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
